import { useState } from 'react';
import { useCompetitions } from '@/hooks/useCompetitions';
import { useReportsByCompetition, useReport, useTriggerReport } from '@/hooks/useReports';
import { PageSpinner } from '@/components/ui/Spinner';
import { ErrorState, EmptyState, Alert } from '@/components/ui/Alert';
import { getApiErrorMessage } from '@/api/client';
import { Card, CardBody } from '@/components/ui/Card';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { formatDateTime } from '@/lib/dates';
import { CompetitionStatus, ReportStatus, ReportType, type ReportRecord } from '@/api/types';

const REPORT_TYPE_OPTIONS: { value: ReportType; label: string; description: string }[] = [
  {
    value: ReportType.CompetitionSummary,
    label: 'Povzetek tekmovanja',
    description:
      'Pregled za organizatorja: število prispevkov po kategorijah (sprejeti, zavrnjeni, nepregledani), napredek sodnikov in najboljši trije v vsaki kategoriji.',
  },
  {
    value: ReportType.JudgingResults,
    label: 'Rezultati ocenjevanja',
    description:
      'Lestvica po kategorijah: mesto, prispevek, avtor, ocena vsakega sodnika in povprečje. Vključeni so samo sprejeti prispevki.',
  },
];

function reportTypeLabel(type: ReportType): string {
  return REPORT_TYPE_OPTIONS.find((o) => o.value === type)?.label ?? 'Poročilo';
}

function reportStatusLabel(status: ReportStatus): string {
  switch (status) {
    case ReportStatus.Pending:
      return 'Čaka';
    case ReportStatus.Processing:
      return 'Ustvarjanje…';
    case ReportStatus.Completed:
      return 'Pripravljeno';
    case ReportStatus.Failed:
      return 'Neuspešno';
    default:
      return 'Neznano';
  }
}

/** Polls a single triggered report until it's Completed/Failed, then shows the download link. */
function ReportRow({ reportId }: { reportId: string }) {
  const report = useReport(reportId);
  if (report.isLoading || !report.data) return <PageSpinner />;

  const r = report.data;
  return (
    <Card>
      <CardBody className="flex items-center justify-between">
        <div>
          <p className="font-medium text-fg">{reportTypeLabel(r.type)}</p>
          <p className="text-sm text-fg-muted">
            {formatDateTime(r.createdAt)} · {reportStatusLabel(r.status)}
            {r.status === ReportStatus.Failed && r.failureReason ? ` — ${r.failureReason}` : ''}
          </p>
        </div>
        {r.status === ReportStatus.Completed && r.downloadUrl && (
          <a href={r.downloadUrl} target="_blank" rel="noreferrer">
            <Button variant="secondary" size="sm">
              Prenesi
            </Button>
          </a>
        )}
      </CardBody>
    </Card>
  );
}

export function OrganizerReportsPage() {
  const competitions = useCompetitions({ page: 1, pageSize: 100 });
  const [competitionId, setCompetitionId] = useState<string>('');
  const [reportType, setReportType] = useState<ReportType>(ReportType.CompetitionSummary);

  const reports = useReportsByCompetition(competitionId || undefined);
  const triggerReport = useTriggerReport();

  function handleTrigger() {
    if (!competitionId) return;
    triggerReport.mutate({ competitionId, type: reportType });
  }

  const selectedCompetition = competitions.data?.items.find((c) => c.id === competitionId);
  const selectedType = REPORT_TYPE_OPTIONS.find((o) => o.value === reportType);
  const resultsNotFinal =
    reportType === ReportType.JudgingResults &&
    selectedCompetition !== undefined &&
    selectedCompetition.status < CompetitionStatus.Awarding;

  const reportItems: ReportRecord[] = reports.data?.items ?? [];

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-xl font-semibold text-fg">Poročila</h1>
      <p className="text-sm text-fg-muted">
        Poročila se ustvarjajo asinhrono - sprožite ga spodaj in ko bo pripravljeno za prenos, se bo prikazalo na
        seznamu.
      </p>

      <Card>
        <CardBody className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1">
            <Select
              label="Tekmovanje"
              value={competitionId}
              onChange={(e) => setCompetitionId(e.target.value)}
              disabled={competitions.isLoading}
            >
              <option value="">Izberite tekmovanje&hellip;</option>
              {competitions.data?.items.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </div>
          <div className="flex-1">
            <Select
              label="Vrsta poročila"
              value={reportType}
              onChange={(e) => setReportType(Number(e.target.value) as ReportType)}
            >
              {REPORT_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </div>
          <Button
            onClick={handleTrigger}
            disabled={!competitionId}
            isLoading={triggerReport.isPending}
            className="self-start"
          >
            Ustvari poročilo
          </Button>
        </CardBody>
        <CardBody className="flex flex-col gap-1 pt-0 text-sm text-fg-muted">
          <p>{selectedType?.description}</p>
          {resultsNotFinal && (
            <p className="text-fg">
              Ocenjevanje še ni zaključeno, zato bodo rezultati označeni kot začasni.
            </p>
          )}
        </CardBody>
        {triggerReport.isError && <Alert variant="error">{getApiErrorMessage(triggerReport.error)}</Alert>}
      </Card>

      {!competitionId && <EmptyState title="Izberite tekmovanje za ogled ali ustvarjanje poročil" />}

      {competitionId && reports.isLoading && <PageSpinner />}
      {competitionId && reports.isError && (
        <ErrorState message={getApiErrorMessage(reports.error)} onRetry={() => reports.refetch()} />
      )}
      {competitionId && reports.isSuccess && reportItems.length === 0 && (
        <EmptyState title="Še ni ustvarjenih poročil" description="Zgoraj ustvarite poročilo." />
      )}
      {competitionId && reports.isSuccess && reportItems.length > 0 && (
        <div className="flex flex-col gap-2">
          {reportItems.map((r) => (
            <ReportRow key={r.id} reportId={r.id} />
          ))}
        </div>
      )}
    </div>
  );
}
